<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onMounted, inject, ref } from "vue"
import { useMachine } from "@xstate/vue"
import { useI18n } from "vue-i18n"

import {
    useSensorFormMachine,
    type SensorFormInput,
    type SearchTaskResult,
    type ManualInputData,
    type InformationInputData,
} from "@core/machines/sensor-form.machine"
import { useRebuildDataTask } from "@core/composables"
import { SourceService } from "@core/services"
import { useSourceStore } from "@core/stores/source.store"
import { useTaskStore } from "@core/stores/task.store"
import type {
    Source,
    Address,
    SourceLegacy,
    SourceLegacyOwnerCompany,
} from "@types"
import { Services } from "@services"
import type { SourceAppendDonePayload } from "@core/components/source/form/append/source-append.types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            loading: {
                creating: "Création du capteur en cours...",
                updating: "Mise à jour du capteur en cours...",
            },
            success: {
                title: "Capteur enregistré",
                message_create: "Le capteur a été créé avec succès.",
                message_update: "Le capteur a été mis à jour avec succès.",
            },
            task_update_data: {
                label: "Récupération des données du capteur",
                error: "La récupération des données du capteur a échoué",
                success:
                    "La récupération des données du capteur a été effectuée avec succès",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    /**
     * Identifiant du bâtiment (pour création)
     */
    buildingId?: string
    /**
     * Identifiant de la source (pour édition)
     */
    sourceId?: string
    /**
     * Adresse du capteur (pour création)
     */
    address?: Address
    /**
     * Cache le bouton d'annulation
     */
    hideCancel?: boolean
    /**
     * Désactive le rebuild après création
     */
    disableRebuild?: boolean
    /**
     * Entrée directe capteur sans sélecteur de type : Retour shell masqué
     */
    appendSkippedTypeSelector?: boolean
    /**
     * Affiche le bouton Retour du shell
     */
    showBack?: boolean
}

interface Emits {
    /**
     * Émis lorsque l'utilisateur clique sur Retour
     */
    (e: "back"): void
    /**
     * Émis lorsque le formulaire est annulé
     */
    (e: "cancel"): void
    /**
     * Émis lorsque la source est créée/mise à jour
     */
    (e: "done", _id_source?: string, source?: SourceAppendDonePayload): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    buildingId: undefined,
    sourceId: undefined,
    address: undefined,
    hideCancel: false,
    disableRebuild: false,
    appendSkippedTypeSelector: false,
    showBack: false,
})

const emit = defineEmits<Emits>()
const hasEmittedDone = ref<boolean>(false)

// ================================
// COMPOSABLES
// ================================
const services = inject<Services>("services")
const sourceStore = useSourceStore()
const taskStore = useTaskStore()
const sourceService = new SourceService()
const { launchRebuildDataTask } = useRebuildDataTask({
    taskname: "Récupération des données du capteur",
})

// ================================
// ÉTAT - MACHINE XSTATE
// ================================

/**
 * Source existante récupérée depuis le store (mode édition)
 */
const getExistingSource = computed<Source | null>(() => {
    if (!props.sourceId) return null
    return sourceStore.getSourceById(props.sourceId) || null
})

/**
 * Input pour initialiser la machine
 */
const getMachineInput = computed<SensorFormInput>(() => ({
    sourceId: props.sourceId,
    buildingId: props.buildingId,
    address: props.address as Record<string, unknown>,
    existingSource: getExistingSource.value,
}))

/**
 * Callback appelé lorsque le capteur est créé/mis à jour
 */
function onUpdateSensor(source: SourceLegacy): void {
    const sourceId = source?._id_source ?? (source as { _id?: string })?._id
    if (!sourceId) return
    const operator =
        (source as { operator?: string })?.operator ??
        source?.informations?.iot?.operator
    const model =
        (source as { model?: string })?.model ??
        source?.informations?.iot?.model
    if (!hasEmittedDone.value) {
        emit("done", sourceId, {
            _id_source: sourceId,
            idsource: source.idsource,
            type: "source",
            idtype: "source",
            name: source.name,
            operator,
            details: { model },
            isInvitationSent: false,
        })
        hasEmittedDone.value = true
    }
    if (!props.disableRebuild) {
        launchRebuildDataTask(sourceId)
    }
    sourceStore.fetchSourceById({ _id_source: sourceId })
}

/**
 * Callback appelé lorsque l'opération est terminée avec succès
 */
function onDone(source: Source): void {
    const sourceId =
        source?._id_source ?? (source as { _id?: string })?._id ?? undefined
    const operator =
        (source as { operator?: string })?.operator ??
        source?.informations?.iot?.operator
    const model =
        (source as { model?: string })?.model ??
        source?.informations?.iot?.model
    if (!hasEmittedDone.value) {
        emit(
            "done",
            sourceId,
            sourceId
                ? {
                      _id_source: sourceId,
                      idsource: source.idsource,
                      type: "source",
                      idtype: "source",
                      name: source.name,
                      operator,
                      details: { model },
                      isInvitationSent: false,
                  }
                : undefined
        )
        hasEmittedDone.value = true
    }
}

/**
 * Callback appelé lorsque l'opération est annulée
 */
function onCancel(): void {
    emit("cancel")
}

/**
 * Callback appelé lorsque l'utilisateur clique sur Retour
 */
function onBack(): void {
    emit("back")
}

/**
 * Lance la tâche getlastdata_updateapp côté front et suit la notification lorsque l'API renvoie meta.should_launch_getlastdata_task: true
 */
async function onFollowSensorTask(output: {
    data: SourceLegacy
    meta: { should_launch_getlastdata_task: boolean }
}): Promise<void> {
    if (!output?.meta?.should_launch_getlastdata_task) return
    const data = output.data as SourceLegacy & { _id?: string }
    const _id_source = data?._id ?? data?._id_source
    if (!_id_source) return
    try {
        // DEV-967 — collecte on-demand via le routing api-metier
        // (`/task/update/source`, source→collecteur) au lieu du getlastdata
        // legacy en direct. On suit le task_id renvoyé (toast + subscribe).
        const { task_id } =
            await sourceService.triggerSourceDataUpdate(_id_source)
        if (!task_id) {
            throw new Error("Aucun task_id renvoyé par la collecte")
        }
        taskStore.followEvent(task_id, task_id, {
            taskToastTitle: t("task_update_data.label"),
            notification: true,
            taskToastFailureMessage: t("task_update_data.error"),
            taskToastSuccessMessage: t("task_update_data.success"),
            autoDelete: true,
        })
    } catch (error) {
        console.error(
            "Erreur lors du lancement de la collecte de données:",
            error
        )
    }
}

/**
 * Crée la machine XState pour le formulaire de capteur
 */
const { machine } = useSensorFormMachine({
    services,
    onDone,
    onCancel,
    onUpdateSensor,
    onFollowSensorTask,
})

/**
 * Instance de la machine avec useMachine de @xstate/vue
 */
const { snapshot, send } = useMachine(machine, {
    input: getMachineInput.value,
})

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Vérifie si on est en mode édition
 */
const getIsEditMode = computed((): boolean => {
    return !!props.sourceId
})

/**
 * État actuel de la machine
 */
const getCurrentState = computed((): string => {
    return snapshot.value.value as string
})

/**
 * Contexte actuel de la machine
 */
const getContext = computed(() => {
    return snapshot.value.context
})

/**
 * Message d'erreur actuel
 */
const getError = computed((): string | null => {
    return getContext.value.error
})

/**
 * Source en cours de création/modification
 */
const getSource = computed(() => {
    return getContext.value.source
})

/**
 * Informations sur le propriétaire actuel
 */
const getCurrentOwner = computed<SourceLegacyOwnerCompany | null>(() => {
    return getExistingSource.value?.owner as SourceLegacyOwnerCompany | null
})

/**
 * Vérifie si on est en état de chargement
 */
const getIsLoading = computed((): boolean => {
    return getCurrentState.value === "loading"
})

/**
 * Vérifie si on est en état terminé
 */
const getIsDone = computed((): boolean => {
    return getCurrentState.value === "done"
})

/**
 * Vérifie si on est en état annulé
 */
const getIsCancelled = computed((): boolean => {
    return getCurrentState.value === "cancelled"
})

/**
 * Message de chargement
 */
const getLoadingMessage = computed((): string => {
    return getIsEditMode.value ? t("loading.updating") : t("loading.creating")
})

// ================================
// EXPOSE (pour parent, ex. SourceAppendDialog)
// ================================

defineExpose({
    /**
     * Indique si le formulaire est en cours de création/mise à jour (état loading de la machine)
     */
    getIsLoading,
})

// ================================
// INTERACTIONS - SENSOR FORM SEARCH
// ================================

/**
 * Gère le changement de l'ID source dans le formulaire de recherche
 */
function onInputIdSource(value: string): void {
    send({ type: "INPUT_IDSOURCE", value })
}

/**
 * Gère le résultat de la recherche de capteur
 */
function onSearchTaskResult(value: SearchTaskResult): void {
    send({ type: "SEARCH_TASK_RESULT", value })
}

/**
 * Gère le clic sur le bouton de saisie manuelle
 */
function onClickManually(): void {
    send({ type: "REDIRECT_MANUALLY" })
}

// ================================
// INTERACTIONS - SENSOR FORM SEARCH MANUAL
// ================================

/**
 * Gère la soumission du formulaire de saisie manuelle
 */
function onSubmitManual(value: ManualInputData): void {
    send({ type: "INPUT_MANUALLY", value })
}

/**
 * Gère l'annulation du formulaire de saisie manuelle
 */
function onCancelManual(): void {
    send({ type: "CANCEL" })
}

// ================================
// INTERACTIONS - SENSOR FORM INFORMATION
// ================================

/**
 * Gère la soumission du formulaire d'information
 */
function onSubmitInformation(value: InformationInputData): void {
    send({ type: "INPUT_INFORMATION", value })
}

/**
 * Gère le clic sur le bouton de modification de l'opérateur
 */
function onClickChangeOperator(): void {
    send({ type: "MODIFY_OPERATOR" })
}

/**
 * Gère l'annulation du formulaire d'information
 */
function onCancelInformation(): void {
    send({ type: "CANCEL" })
}

/**
 * Retour depuis l’étape titulaire vers le sélecteur de type (modale append) : sort du formulaire capteur.
 */
function onOwnerStepAppendBack(): void {
    emit("cancel")
}

// ================================
// LIFECYCLE
// ================================

/**
 * Démarre la machine au montage du composant
 */
onMounted(() => {
    send({ type: "START" })
})
</script>

<template>
    <div class="sensor-form w-full max-w-[600px] flex flex-col gap-6">
        <!-- ================================ -->
        <!-- MESSAGE D'ERREUR -->
        <!-- ================================ -->
        <BaseAppMessage
            v-if="getError && !getIsLoading && !getIsDone"
            severity="error"
            :message="getError"
        />

        <!-- ================================ -->
        <!-- ÉTAT: RECHERCHE PAR TÂCHE -->
        <!-- ================================ -->
        <SensorFormSearch
            v-if="getCurrentState === 'SensorFormSearchTask'"
            :value="getSource?.idsource"
            :is-editing="getIsEditMode"
            @input="onInputIdSource"
            @result="onSearchTaskResult"
            @click:manually="onClickManually"
        />

        <!-- ================================ -->
        <!-- ÉTAT: SAISIE MANUELLE -->
        <!-- ================================ -->
        <SensorFormSearchManual
            v-else-if="getCurrentState === 'SensorFormSearchManually'"
            :value="{
                idsource: getSource?.idsource,
                operator: getSource?.operator,
                apikey: getSource?.apikey,
            }"
            @click:submit="onSubmitManual"
            @click:cancel="onCancelManual"
        />

        <!-- ================================ -->
        <!-- ÉTAT: INFORMATIONS DU CAPTEUR -->
        <!-- ================================ -->
        <SensorFormInformation
            v-else-if="getCurrentState === 'SensorFormInformation'"
            :value="{
                idsource: getSource?.idsource,
                operator: getSource?.operator,
                brand: getSource?.brand,
                model: getSource?.model,
                channels: getSource?.channels,
                name: getSource?.name,
                owner: getSource?.owner,
            }"
            :models="getSource?.models ?? []"
            :hide-cancel="hideCancel"
            required-owner
            :current-owner="getCurrentOwner"
            :is-editing="getIsEditMode"
            :append-skipped-type-selector="appendSkippedTypeSelector"
            @click:submit="onSubmitInformation"
            @click:change-operator="onClickChangeOperator"
            @click:cancel="onCancelInformation"
            @owner-step-append-back="onOwnerStepAppendBack"
        />

        <!-- ================================ -->
        <!-- ÉTAT: CHARGEMENT -->
        <!-- ================================ -->
        <div
            v-else-if="getIsLoading"
            class="flex flex-col items-center justify-center gap-4 py-14"
        >
            <BaseProgressBar mode="indeterminate" class="w-full max-w-xs" />
            <p class="text-gray-600 text-center">
                {{ getLoadingMessage }}
            </p>
        </div>

        <!-- ================================ -->
        <!-- ÉTAT: TERMINÉ -->
        <!-- ================================ -->
        <div
            v-else-if="getIsDone"
            class="flex flex-col items-center justify-center gap-4 py-14"
        >
            <div
                class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center"
            >
                <i class="pi pi-check text-green-600 text-2xl"></i>
            </div>
            <h2 class="text-xl font-semibold text-gray-700">
                {{ t("success.title") }}
            </h2>
            <p class="text-gray-600 text-center">
                {{
                    getIsEditMode
                        ? t("success.message_update")
                        : t("success.message_create")
                }}
            </p>
        </div>

        <!-- ================================ -->
        <!-- ÉTAT: ANNULÉ -->
        <!-- ================================ -->
        <div
            v-else-if="getIsCancelled"
            class="flex flex-col items-center justify-center gap-4 py-14"
        >
            <p class="text-gray-500 text-center">Opération annulée.</p>
        </div>

        <!-- ================================ -->
        <!-- ÉTAT: IDLE (en attente) -->
        <!-- ================================ -->
        <div
            v-else-if="getCurrentState === 'idle'"
            class="flex flex-col items-center justify-center gap-4 py-14"
        >
            <BaseProgressBar mode="indeterminate" class="w-full max-w-xs" />
            <p class="text-gray-600 text-center">Initialisation...</p>
        </div>

        <!-- ================================ -->
        <!-- ACTION: RETOUR -->
        <!-- ================================ -->
        <div
            v-if="
                showBack &&
                !appendSkippedTypeSelector &&
                !getIsLoading &&
                !getIsDone &&
                !getIsCancelled
            "
        >
            <BaseButton
                label="Retour"
                severity="secondary"
                @click="onBack"
                class="min-w-[10rem] self-center"
            />
        </div>
    </div>
</template>
